import React, { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import Icon from "@mdi/react";
import { mdiClose, mdiHelpCircleOutline } from "@mdi/js";
import MarkupCanvas, { MIN_ARROW_LENGTH } from "./MarkupCanvas";
import MarkupToolbar from "./MarkupToolbar";
import AnnotationGuide, { hasSeenGuide, markGuideSeen } from "./AnnotationGuide";
import {
  deleteAnnotatedImage,
  fetchImageWithAnnotations,
  saveAnnotatedImage,
} from "../slice";

const newArrowId = () =>
  `arrow-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const HISTORY_LIMIT = 50;

// BE expects integer image-space coords within the image bounds. Arrows can
// be dragged partially off-canvas, so clamp on the way out.
export const buildAnnotations = (arrows, imageSize) => {
  const maxX = imageSize?.width ?? Infinity;
  const maxY = imageSize?.height ?? Infinity;
  const clamp = (value, max) => Math.min(Math.max(0, Math.round(value)), max);
  return arrows.map((a, idx) => ({
    annotation_type: "arrow",
    x1: clamp(a.x1, maxX),
    y1: clamp(a.y1, maxY),
    x2: clamp(a.x2, maxX),
    y2: clamp(a.y2, maxY),
    z_index: idx + 1,
    color: "#FF0000",
    arrow_style: "solid",
    arrow_head_style: "standard",
  }));
};

// Local mode (Add New flow): the photo isn't on the server yet, so there's no
// image id to annotate. Pass `localImageUrl` + `localArrows` and an
// `onLocalComplete(arrows, blob)` callback — the editor hydrates from the
// in-memory arrows and hands back a baked blob (or null when cleared) instead
// of saving to the server. No toast, no banner (see AS-892/894 follow-up).
const EditPhotoMarkup = ({
  show,
  image,
  onClose,
  onToast,
  localImageUrl,
  localArrows,
  onLocalComplete,
}) => {
  const localMode = typeof onLocalComplete === "function";
  const dispatch = useDispatch();
  const canvasRef = useRef(null);
  // Pre-drag snapshot — one undo entry per drag gesture, not per frame.
  const dragOriginRef = useRef(null);
  // Mirrors `inFlight` so pointer handlers read the latest value without
  // committing history inside a setState updater (impure under StrictMode).
  const inFlightRef = useRef(null);
  const [showGuide, setShowGuide] = useState(false);
  const [arrows, setArrows] = useState([]);
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [inFlight, setInFlight] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isHydrating, setIsHydrating] = useState(false);

  // Reset every interaction state when the modal opens with a new image,
  // then hydrate saved arrows if the photo already has annotations.
  useEffect(() => {
    if (!show) return undefined;
    setArrows([]);
    setPast([]);
    setFuture([]);
    setInFlight(null);
    setSelectedId(null);
    dragOriginRef.current = null;
    inFlightRef.current = null;
    if (!hasSeenGuide()) setShowGuide(true);

    // Local mode: hydrate from the in-memory arrows, no server fetch.
    if (localMode) {
      setArrows(localArrows || []);
      setIsHydrating(false);
      return undefined;
    }

    if (!image?.id || !image?.annotated_image) {
      setIsHydrating(false);
      return undefined;
    }
    let cancelled = false;
    setIsHydrating(true);
    dispatch(fetchImageWithAnnotations(image.id))
      .unwrap()
      .then((data) => {
        if (cancelled) return;
        const list = data?.annotated_image?.annotations || [];
        const hydrated = list
          .filter((a) => a?.annotation_type === "arrow")
          .map((a, idx) => ({
            id: `arrow-saved-${idx}`,
            x1: a.x1,
            y1: a.y1,
            x2: a.x2,
            y2: a.y2,
          }));
        setArrows(hydrated);
      })
      .catch(() => {
        if (cancelled) return;
        onToast?.({
          message: "Could not load existing markup. Starting fresh.",
          variant: "error",
        });
      })
      .finally(() => {
        if (!cancelled) setIsHydrating(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, image?.id, localImageUrl]);

  // Push `arrows` onto the undo stack and replace with `next`. Any redo
  // stack is dropped on a fresh action.
  const commit = (next) => {
    setPast((prev) => {
      const stacked = [...prev, arrows];
      return stacked.length > HISTORY_LIMIT
        ? stacked.slice(stacked.length - HISTORY_LIMIT)
        : stacked;
    });
    setFuture([]);
    setArrows(next);
  };

  const handleDragSessionStart = () => {
    dragOriginRef.current = arrows;
  };
  const handleDragSessionEnd = () => {
    const origin = dragOriginRef.current;
    dragOriginRef.current = null;
    if (!origin) return;
    setPast((prev) => {
      const stacked = [...prev, origin];
      return stacked.length > HISTORY_LIMIT
        ? stacked.slice(stacked.length - HISTORY_LIMIT)
        : stacked;
    });
    setFuture([]);
  };

  const handleSelectArrow = (id) => setSelectedId(id);
  const handleDeselectArrow = () => setSelectedId(null);

  const handleMoveArrow = (id, dx, dy) => {
    setArrows((prev) =>
      prev.map((a) =>
        a.id === id
          ? { ...a, x1: a.x1 + dx, y1: a.y1 + dy, x2: a.x2 + dx, y2: a.y2 + dy }
          : a
      )
    );
  };

  const handleMoveAnchor = (id, which, x, y) => {
    setArrows((prev) =>
      prev.map((a) => {
        if (a.id !== id) return a;
        return which === "start"
          ? { ...a, x1: x, y1: y }
          : { ...a, x2: x, y2: y };
      })
    );
  };

  const handleDismissGuide = () => {
    markGuideSeen();
    setShowGuide(false);
  };

  const handleOpenGuide = () => setShowGuide(true);

  const handleStart = (pt) => {
    if (showGuide || isHydrating) return;
    const arrow = { x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y };
    inFlightRef.current = arrow;
    setInFlight(arrow);
  };
  const handleMove = (pt) => {
    const cur = inFlightRef.current;
    if (!cur) return;
    const next = { ...cur, x2: pt.x, y2: pt.y };
    inFlightRef.current = next;
    setInFlight(next);
  };
  const handleEnd = (pt) => {
    const cur = inFlightRef.current;
    if (!cur) return;
    inFlightRef.current = null;
    setInFlight(null);
    // `pt` is the Stage release point; when the gesture ends off-canvas (window
    // fallback) it's omitted, so use the in-flight arrow's last tracked end.
    const x2 = pt ? pt.x : cur.x2;
    const y2 = pt ? pt.y : cur.y2;
    if (Math.hypot(x2 - cur.x1, y2 - cur.y1) >= MIN_ARROW_LENGTH) {
      commit([...arrows, { id: newArrowId(), x1: cur.x1, y1: cur.y1, x2, y2 }]);
    }
  };

  // A draw released outside the canvas never fires the Stage's pointerup, so
  // the arrow would stay in-flight: baked into the saved image by toBlob but
  // never committed to `arrows` (and thus missing from the annotations JSON) —
  // making the saved image and the editor disagree on arrow count (AS-904).
  // Finalize on a window-level pointerup so every gesture commits. Releasing
  // inside the canvas fires both handlers; handleEnd no-ops on the second.
  useEffect(() => {
    if (!inFlight) return undefined;
    const onWindowPointerUp = () => handleEnd();
    window.addEventListener("pointerup", onWindowPointerUp);
    return () => window.removeEventListener("pointerup", onWindowPointerUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inFlight, arrows]);

  const handleUndo = () => {
    if (past.length === 0) return;
    const previous = past[past.length - 1];
    setPast((prev) => prev.slice(0, -1));
    setFuture((prev) => [arrows, ...prev]);
    setArrows(previous);
    setSelectedId(null);
  };

  const handleRedo = () => {
    if (future.length === 0) return;
    const next = future[0];
    setFuture((prev) => prev.slice(1));
    setPast((prev) => [...prev, arrows]);
    setArrows(next);
    setSelectedId(null);
  };

  const handleClear = () => {
    if (arrows.length === 0) return;
    commit([]);
    setSelectedId(null);
  };

  const handleComplete = async () => {
    if (isSaving) return;
    // Local mode: bake the current arrows into a blob (null when cleared) and
    // hand it back. Applied in-memory only — no server save, toast, or banner.
    if (localMode) {
      setIsSaving(true);
      try {
        const imageSize = canvasRef.current?.getImageSize();
        let blob = null;
        if (arrows.length > 0) {
          blob = await canvasRef.current?.toBlob();
          if (!blob) throw new Error("Canvas not ready");
        }
        onLocalComplete(arrows, blob, imageSize);
        onClose();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Markup apply failed:", err);
        onToast?.({
          message: "Failed to apply markup. Please try again.",
          variant: "error",
        });
      } finally {
        setIsSaving(false);
      }
      return;
    }
    if (!image?.id) return;
    const hadAnnotation = !!image?.annotated_image;
    // Nothing to persist: no arrows and the photo had no saved annotation.
    if (arrows.length === 0 && !hadAnnotation) return;
    setIsSaving(true);
    try {
      if (arrows.length === 0) {
        // Cleared every arrow — remove the saved annotation entirely.
        await dispatch(deleteAnnotatedImage({ imageId: image.id })).unwrap();
      } else {
        const imageSize = canvasRef.current?.getImageSize();
        const file = await canvasRef.current?.toBlob();
        if (!file) throw new Error("Canvas not ready");
        await dispatch(
          saveAnnotatedImage({
            imageId: image.id,
            file,
            annotations: buildAnnotations(arrows, imageSize),
          })
        ).unwrap();
      }
      onToast?.({ message: "Markup saved successfully.", variant: "success" });
      onClose();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Markup save failed:", err);
      onToast?.({
        message: "Failed to save markup. Please try again.",
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Always edit on the clean base image. The annotated image has arrows baked
  // into its pixels; using it as the canvas background would duplicate the
  // saved arrows (which are hydrated separately as editable overlays) — moving
  // one would leave the baked copy behind. See AS-892. Local mode supplies its
  // own (base) object URL.
  const imageUrl = localMode
    ? localImageUrl || null
    : image?.presigned_url || null;

  return (
    <Modal
      show={show}
      onHide={onClose}
      fullscreen
      backdrop="static"
      className="markup-modal"
    >
      <Modal.Body className="markup-modal-body">
        <div className="markup-modal-top-actions">
          <Button
            variant="link"
            className="markup-modal-icon-btn"
            onClick={handleOpenGuide}
            aria-label="Show annotation guide"
          >
            <Icon path={mdiHelpCircleOutline} size={1} />
          </Button>
          <Button
            variant="link"
            className="markup-modal-icon-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <Icon path={mdiClose} size={1} />
          </Button>
        </div>

        <MarkupCanvas
          ref={canvasRef}
          imageUrl={imageUrl}
          arrows={arrows}
          inFlight={inFlight}
          selectedId={selectedId}
          onStart={handleStart}
          onMove={handleMove}
          onEnd={handleEnd}
          onSelect={handleSelectArrow}
          onDeselect={handleDeselectArrow}
          onMoveArrow={handleMoveArrow}
          onMoveAnchor={handleMoveAnchor}
          onDragSessionStart={handleDragSessionStart}
          onDragSessionEnd={handleDragSessionEnd}
        />
      </Modal.Body>
      <div className="markup-modal-bottom">
        <MarkupToolbar
          canUndo={past.length > 0 && !isHydrating}
          canRedo={future.length > 0 && !isHydrating}
          canClear={arrows.length > 0 && !isHydrating}
          canComplete={
            (arrows.length > 0 ||
              (localMode
                ? (localArrows?.length ?? 0) > 0
                : !!image?.annotated_image)) &&
            !isHydrating
          }
          isSaving={isSaving}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onClear={handleClear}
          onComplete={handleComplete}
        />
      </div>

      {showGuide && <AnnotationGuide onDismiss={handleDismissGuide} />}
    </Modal>
  );
};

export default EditPhotoMarkup;
